/**
 * Estado de CONFERÊNCIA do reajuste da assinatura.
 *
 * O índice e a data-base do cadastro podem ser chute: em 03/09/2026, de sete
 * contratos públicos abertos contra o edital, três estavam errados. Enquanto
 * "não conferido" não existia como estado, um contrato certo e um errado tinham
 * a mesma aparência, e o card de reajuste vencido apontava para 102 dos 142
 * contratos ativos, ou seja, para nada.
 *
 * Mantido em sincronia com ADJUSTMENT_VERIFICATION_STATUSES do backend
 * (src/contracts/contract.entity.ts) e com o CHECK da migration
 * AdjustmentVerificationState.
 */
export type AdjustmentVerificationStatus =
  | 'nao_conferido'
  | 'conferido'
  | 'devido'
  | 'oficio_enviado'
  | 'apostila_recebida'
  | 'aplicado';

type Meta = {
  label: string;
  short: string;
  badge: string;
  hint: string;
};

export const ADJUSTMENT_VERIFICATION_META: Record<AdjustmentVerificationStatus, Meta> = {
  nao_conferido: {
    // ⚠️ O texto não pode afirmar que a regra "nunca foi lida": este estado é o
    // único que vence todos os outros (ver deriveVerificationStatus), então um
    // contrato com índice, data-base e fonte preenchidos cai aqui só por faltar
    // o carimbo de quem conferiu. Em 19/09/2026 a tooltip dizia "nunca foram
    // lidos" logo acima de "Fonte: Contrato 62/2023", contradizendo a si mesma.
    label: 'Não conferido',
    short: 'N/C',
    badge: 'bg-slate-700 text-slate-300',
    hint: 'Ninguém confirmou ter lido índice e data-base no contrato ou no Termo de Referência. Enquanto isso, trate os dois como não confiáveis, mesmo que já estejam preenchidos.',
  },
  conferido: {
    label: 'Conferido',
    short: 'OK',
    badge: 'bg-green-500/20 text-green-400',
    hint: 'Índice e data-base lidos no documento e corretos. Sem reajuste devido agora.',
  },
  devido: {
    label: 'Reajuste devido',
    short: 'Devido',
    badge: 'bg-red-500/20 text-red-400',
    hint: 'Anualidade implementada e reajuste ainda não pedido. Cada competência faturada sai a menos.',
  },
  oficio_enviado: {
    label: 'Ofício enviado',
    short: 'Ofício',
    badge: 'bg-amber-500/20 text-amber-400',
    hint: 'Pedido protocolado, aguardando decisão do órgão. Continuar faturando o valor antigo.',
  },
  apostila_recebida: {
    label: 'Apostila recebida',
    short: 'Apostila',
    badge: 'bg-blue-500/20 text-blue-400',
    hint: 'Órgão deferiu. Falta aplicar o valor no cadastro e cobrar o retroativo.',
  },
  aplicado: {
    label: 'Aplicado',
    short: 'Aplicado',
    badge: 'bg-emerald-500/20 text-emerald-400',
    hint: 'Valor novo em vigor e retroativo faturado. Ciclo fechado.',
  },
};

export const ADJUSTMENT_VERIFICATION_OPTIONS = (
  Object.keys(ADJUSTMENT_VERIFICATION_META) as AdjustmentVerificationStatus[]
).map((value) => ({ value, label: ADJUSTMENT_VERIFICATION_META[value].label }));

export function adjustmentVerificationMeta(status?: string | null): Meta {
  return (
    ADJUSTMENT_VERIFICATION_META[status as AdjustmentVerificationStatus] ||
    ADJUSTMENT_VERIFICATION_META.nao_conferido
  );
}

/** Já passou pela leitura do documento, então índice e data-base valem algo. */
export function isAdjustmentVerified(status?: string | null): boolean {
  return !!status && status !== 'nao_conferido';
}

/**
 * Reajuste devido de verdade: a data-base venceu E alguém já confirmou que a
 * data-base é essa. Sem o segundo teste o número vira ruído.
 */
export function isAdjustmentDue(contract: any, daysUntil: number | null): boolean {
  if (contract?.status !== 'Ativo') return false;
  if (!contract?.nextAdjustmentDate || daysUntil === null || daysUntil > 0) return false;
  if (!isAdjustmentVerified(contract.adjustmentVerificationStatus)) return false;
  return contract.adjustmentVerificationStatus !== 'aplicado';
}
